/**
 * React hook wrapper around `subscribeTableRows` — the single primitive every
 * "frontend gets notified live" feature should reach for.
 *
 * Lifecycle:
 *   - subscribes on mount (or when `enabled` flips true)
 *   - tears down on unmount (or when `enabled` flips false, or when any
 *     identity field — channelName / schema / table / filter — changes)
 *   - keeps handlers fresh via refs so callers can pass inline closures
 *     without thrashing the subscription on every render
 *
 * Whether each event type is wired is captured at first subscribe; consumers
 * that need to dynamically add/remove an event handler should bump
 * `channelName` (or toggle `enabled`) to force a re-subscribe. This trade-off
 * keeps the common case (consistent handler shape across a hook's lifetime)
 * cheap — no extra server-side bindings — while still letting callers
 * upgrade the binding set when the topology genuinely changes.
 */
import { useEffect, useRef } from 'react';

import {
  subscribeTableRows,
  type DatabaseRow,
  type SubscribeTableRowsParams,
} from '@/lib/realtime/subscribeTableRows';

export type UseRealtimeRowsParams<T extends DatabaseRow> =
  SubscribeTableRowsParams<T> & {
    /** Skip subscribing while false. Use to gate on auth context — e.g.
     * only subscribe once `companyId` resolves. */
    enabled?: boolean;
  };

export function useRealtimeRows<T extends DatabaseRow>(
  params: UseRealtimeRowsParams<T>
): void {
  const {
    enabled = true,
    channelName,
    schema,
    table,
    filter,
    onInsert,
    onUpdate,
    onDelete,
  } = params;

  const insertRef = useRef(onInsert);
  const updateRef = useRef(onUpdate);
  const deleteRef = useRef(onDelete);
  insertRef.current = onInsert;
  updateRef.current = onUpdate;
  deleteRef.current = onDelete;

  // Captured-at-subscribe — see the file header for the rationale.
  const wantsInsert = !!onInsert;
  const wantsUpdate = !!onUpdate;
  const wantsDelete = !!onDelete;

  useEffect(() => {
    if (!enabled) return;
    return subscribeTableRows<T>({
      channelName,
      schema,
      table,
      filter,
      onInsert: wantsInsert
        ? (row) => {
            insertRef.current?.(row);
          }
        : undefined,
      onUpdate: wantsUpdate
        ? (newRow, oldRow) => {
            updateRef.current?.(newRow, oldRow);
          }
        : undefined,
      onDelete: wantsDelete
        ? (row) => {
            deleteRef.current?.(row);
          }
        : undefined,
    });
  }, [
    enabled,
    channelName,
    schema,
    table,
    filter,
    wantsInsert,
    wantsUpdate,
    wantsDelete,
  ]);
}
